import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import api from '../lib/api';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { Clock } from 'lucide-react';
import { WelcomeModal } from '../components/WelcomeModal';
import { InteractiveWalkthrough } from '../components/InteractiveWalkthrough';
import { MedalCelebration } from '../components/MedalCelebration';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { MatrixGrid } from '../components/dashboard/MatrixGrid';
import { InstallBanner } from '../components/dashboard/InstallBanner';
import { AddInterestDialog } from '../components/dashboard/AddInterestDialog';
import { EditInterestDialog } from '../components/dashboard/EditInterestDialog';
import { AchieveDialog } from '../components/dashboard/AchieveDialog';
import { WellbeingSurveyDialog } from '../components/dashboard/WellbeingSurveyDialog';

const DEFAULT_NEW_INTEREST = {
  title: '', description: '', quadrant: 'q1',
  deadline: null, deadlineTime: '12:00', hasDeadline: false,
  reminder_minutes: null, repeat: 'none'
};

const DEFAULT_SURVEY = {
  control: 5, productivity: 5, efficiency: 5, contentment: 5,
  self_esteem: 5, concentration: 5, work_life_balance: 5
};

// Resolve overlapping interest positions
const resolveOverlaps = (interestsList) => {
  const minDistance = 10;
  const result = [...interestsList];

  for (let i = 0; i < result.length; i++) {
    let hasCollision = true;
    let iterations = 0;

    while (hasCollision && iterations < 15) {
      hasCollision = false;
      for (let j = 0; j < result.length; j++) {
        if (i === j) continue;
        const dx = result[i].position_x - result[j].position_x;
        const dy = result[i].position_y - result[j].position_y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          hasCollision = true;
          if (distance > 0) {
            const pushX = (dx / distance) * (minDistance - distance + 1);
            const pushY = (dy / distance) * (minDistance - distance + 1);
            result[i] = {
              ...result[i],
              position_x: Math.max(5, Math.min(88, result[i].position_x + pushX)),
              position_y: Math.max(5, Math.min(93, result[i].position_y + pushY))
            };
          } else {
            result[i] = {
              ...result[i],
              position_x: Math.max(5, Math.min(88, result[i].position_x + (Math.random() - 0.5) * minDistance * 2)),
              position_y: Math.max(5, Math.min(93, result[i].position_y + (Math.random() - 0.5) * minDistance * 2))
            };
          }
          break;
        }
      }
      iterations++;
    }
  }
  return result;
};

export const DashboardPage = () => {
  const { user, logout, token } = useAuth();
  const { isInstallable, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const navigate = useNavigate();
  const matrixRef = useRef(null);

  // Core state
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggeredReminders, setTriggeredReminders] = useState(new Set());

  // Dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAchieveDialog, setShowAchieveDialog] = useState(false);
  const [newInterest, setNewInterest] = useState(DEFAULT_NEW_INTEREST);
  const [editingInterest, setEditingInterest] = useState(null);
  const [achievingInterest, setAchievingInterest] = useState(null);
  const [beneficiary, setBeneficiary] = useState('me');

  // Celebration state
  const [showConfetti, setShowConfetti] = useState(false);
  const [earnedMedal, setEarnedMedal] = useState(null);

  // Install banner state
  const [showInstallBanner, setShowInstallBanner] = useState(() => {
    return !localStorage.getItem('install_banner_dismissed');
  });

  // Tutorial state
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    return !localStorage.getItem('welcome_modal_seen');
  });
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // Wellbeing survey state
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [surveyData, setSurveyData] = useState(DEFAULT_SURVEY);
  const surveyCheckedRef = useRef(false);

  // Drag-over state for Achievement Store highlight
  const [dragOverAchieve, setDragOverAchieve] = useState(false);

  // --- Real-time sync ---
  const handleSyncEvent = useCallback((event) => {
    if (event.type === 'interest_update') {
      switch (event.action) {
        case 'moved':
          setInterests(prev => prev.map(i =>
            i.interest_id === event.interest_id
              ? { ...i, position_x: event.position_x, position_y: event.position_y, quadrant: event.quadrant }
              : i
          ));
          break;
        case 'created':
          if (event.interest) {
            setInterests(prev => prev.some(i => i.interest_id === event.interest.interest_id) ? prev : [...prev, event.interest]);
            toast.info('New interest synced from another device');
          }
          break;
        case 'updated':
          if (event.interest) {
            setInterests(prev => prev.map(i =>
              i.interest_id === event.interest.interest_id ? event.interest : i
            ));
          }
          break;
        case 'deleted':
          setInterests(prev => prev.filter(i => i.interest_id !== event.interest_id));
          break;
        case 'achieved':
          setInterests(prev => prev.filter(i => i.interest_id !== event.interest_id));
          toast.success('Interest achieved on another device!');
          break;
        default:
          break;
      }
    }
  }, []);

  const { isConnected, sendSyncEvent, usePolling } = useRealtimeSync(token, handleSyncEvent);

  // --- Data fetching ---
  const fetchInterests = useCallback(async () => {
    try {
      const response = await api.get('/interests');
      let interestsWithPositions = response.data.map((interest, index) => {
        if (interest.position_x === undefined || interest.position_y === undefined) {
          const positions = { q1: { x: 25, y: 25 }, q2: { x: 75, y: 25 }, q3: { x: 25, y: 75 }, q4: { x: 75, y: 75 } };
          const base = positions[interest.quadrant] || positions.q1;
          const offset = (index % 5) * 8;
          return { ...interest, position_x: base.x + offset - 16, position_y: base.y + offset - 16 };
        }
        return interest;
      });
      interestsWithPositions = resolveOverlaps(interestsWithPositions);
      setInterests(interestsWithPositions);
    } catch {
      toast.error('Failed to load interests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInterests(); }, [fetchInterests]);

  // --- Reminder polling ---
  useEffect(() => {
    const checkReminders = async () => {
      try {
        const response = await api.get('/interests/reminders');
        const newTriggered = new Set(triggeredReminders);
        for (const reminder of response.data) {
          newTriggered.add(reminder.interest_id);
          if (!triggeredReminders.has(reminder.interest_id)) {
            toast.warning(`Reminder: "${reminder.title}" deadline approaching!`, {
              duration: 5000,
              icon: <Clock className="w-4 h-4 text-amber-500" />
            });
          }
          await api.post(`/interests/${reminder.interest_id}/dismiss-reminder`, {});
        }
        const now = new Date();
        for (const interest of interests) {
          if (interest.deadline && interest.reminder_minutes) {
            const deadline = new Date(interest.deadline);
            const reminderTime = new Date(deadline.getTime() - interest.reminder_minutes * 60 * 1000);
            if (now >= reminderTime) newTriggered.add(interest.interest_id);
          }
        }
        setTriggeredReminders(newTriggered);
      } catch (err) { console.error('Failed to check reminders:', err); }
    };
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    checkReminders();
    const interval = setInterval(checkReminders, 30000);
    return () => clearInterval(interval);
  }, [interests, triggeredReminders]);

  // --- Wellbeing survey timer ---
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (surveyCheckedRef.current) return;
      surveyCheckedRef.current = true;
      try {
        const res = await api.get('/surveys/should-show');
        if (res.data.should_show) setShowSurveyDialog(true);
      } catch (err) { console.error('Failed to check wellbeing survey status:', err); }
    }, 30 * 60 * 1000);
    return () => clearTimeout(timer);
  }, []);

  // --- Event handlers ---
  const dismissInstallBanner = () => {
    localStorage.setItem('install_banner_dismissed', 'true');
    setShowInstallBanner(false);
  };

  const handleWelcomeComplete = () => setShowWalkthrough(true);
  const replayTutorial = () => setShowWelcomeModal(true);
  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleDragEnd = async (interestId, x, y, quadrant) => {
    try {
      await api.put(`/interests/${interestId}`, { quadrant, position_x: x, position_y: y });
      setInterests(prev => prev.map(i =>
        i.interest_id === interestId ? { ...i, quadrant, position_x: x, position_y: y } : i
      ));
      sendSyncEvent('interest_moved', { interest_id: interestId, position_x: x, position_y: y, quadrant });
    } catch {
      toast.error('Failed to update position');
    }
  };

  const handleAddInterest = async () => {
    if (!newInterest.title.trim()) { toast.error('Please enter a title'); return; }
    try {
      let deadline = null;
      if (newInterest.hasDeadline && newInterest.deadline) {
        const date = new Date(newInterest.deadline);
        const [hours, minutes] = newInterest.deadlineTime.split(':');
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        deadline = date.toISOString();
      }
      const response = await api.post('/interests', {
        title: newInterest.title, description: newInterest.description,
        quadrant: newInterest.quadrant, deadline,
        reminder_minutes: newInterest.hasDeadline ? newInterest.reminder_minutes : null,
        repeat: newInterest.repeat !== 'none' ? newInterest.repeat : null,
        position_x: newInterest.quadrant === 'q1' || newInterest.quadrant === 'q3' ? 25 : 75,
        position_y: newInterest.quadrant === 'q1' || newInterest.quadrant === 'q2' ? 25 : 75
      });
      const newInt = {
        ...response.data,
        position_x: newInterest.quadrant === 'q1' || newInterest.quadrant === 'q3' ? 25 : 75,
        position_y: newInterest.quadrant === 'q1' || newInterest.quadrant === 'q2' ? 25 : 75
      };
      setInterests(prev => prev.some(i => i.interest_id === newInt.interest_id) ? prev : [...prev, newInt]);
      sendSyncEvent('interest_created', { interest: newInt });
      setShowAddDialog(false);
      setNewInterest(DEFAULT_NEW_INTEREST);
      toast.success('Interest added');
    } catch {
      toast.error('Failed to add interest');
    }
  };

  const openEditDialog = (interest) => {
    let deadlineDate = null;
    let deadlineTime = '12:00';
    if (interest.deadline) {
      const date = new Date(interest.deadline);
      deadlineDate = date;
      deadlineTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
    setEditingInterest({
      ...interest, deadline: deadlineDate, deadlineTime,
      hasDeadline: !!interest.deadline, reminder_minutes: interest.reminder_minutes || null,
      repeat: interest.repeat || 'none'
    });
    setShowEditDialog(true);
  };

  const handleEditInterest = async () => {
    if (!editingInterest?.title.trim()) { toast.error('Please enter a title'); return; }
    try {
      let deadline = null;
      if (editingInterest.hasDeadline && editingInterest.deadline) {
        const date = new Date(editingInterest.deadline);
        if (editingInterest.deadlineTime) {
          const [hours, minutes] = editingInterest.deadlineTime.split(':');
          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }
        deadline = date.toISOString();
      }
      await api.put(`/interests/${editingInterest.interest_id}`, {
        title: editingInterest.title, description: editingInterest.description,
        deadline,
        reminder_minutes: editingInterest.hasDeadline ? editingInterest.reminder_minutes : null,
        repeat: editingInterest.repeat !== 'none' ? editingInterest.repeat : 'none'
      });
      const updatedInterest = { ...editingInterest, deadline, repeat: editingInterest.repeat !== 'none' ? editingInterest.repeat : null };
      setInterests(prev => prev.map(i =>
        i.interest_id === editingInterest.interest_id ? { ...i, ...editingInterest, deadline } : i
      ));
      sendSyncEvent('interest_updated', { interest: updatedInterest });
      setShowEditDialog(false);
      setEditingInterest(null);
      toast.success('Interest updated');
    } catch {
      toast.error('Failed to update interest');
    }
  };

  const handleDeleteInterest = async (interestId) => {
    try {
      await api.delete(`/interests/${interestId}`);
      setInterests(prev => prev.filter(i => i.interest_id !== interestId));
      sendSyncEvent('interest_deleted', { interest_id: interestId });
      toast.success('Interest deleted');
    } catch {
      toast.error('Failed to delete interest');
    }
  };

  const handleMarkAsAchieved = (interest) => {
    setAchievingInterest(interest);
    setShowAchieveDialog(true);
  };

  const handleAchieveInterest = async () => {
    if (!achievingInterest) return;
    try {
      const response = await api.post('/achievements', {
        interest_id: achievingInterest.interest_id, beneficiary
      });
      sendSyncEvent('interest_achieved', { interest_id: achievingInterest.interest_id, achievement: response.data });
      setInterests(prev => prev.filter(i => i.interest_id !== achievingInterest.interest_id));
      setShowAchieveDialog(false);
      setAchievingInterest(null);
      setBeneficiary('me');
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
      toast.success('Achievement unlocked!');
      if (response.data.medal) {
        setTimeout(() => setEarnedMedal(response.data.medal), 1500);
      }
    } catch {
      toast.error('Failed to record achievement');
    }
  };

  const handleSubmitSurvey = async () => {
    try {
      await api.post('/surveys/wellbeing', surveyData);
      toast.success('Thank you for your feedback!');
      setShowSurveyDialog(false);
    } catch {
      toast.error('Failed to submit survey');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={200} gravity={0.3} />
      )}

      <DashboardHeader
        user={user}
        isConnected={isConnected}
        usePolling={usePolling}
        onLogout={handleLogout}
        onReplayTutorial={replayTutorial}
        interests={interests}
        isInstallable={isInstallable}
        isInstalled={isInstalled}
        isIOS={isIOS}
        promptInstall={promptInstall}
        dragOverAchieve={dragOverAchieve}
      />

      <InstallBanner
        show={showInstallBanner}
        isInstalled={isInstalled}
        isIOS={isIOS}
        isInstallable={isInstallable}
        promptInstall={promptInstall}
        onDismiss={dismissInstallBanner}
      />

      <MatrixGrid
        ref={matrixRef}
        interests={interests}
        triggeredReminders={triggeredReminders}
        onDragEnd={handleDragEnd}
        onEdit={openEditDialog}
        onAchieve={handleMarkAsAchieved}
        onAddClick={() => setShowAddDialog(true)}
      />

      <AddInterestDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        interest={newInterest}
        onChange={setNewInterest}
        onSubmit={handleAddInterest}
      />

      <EditInterestDialog
        open={showEditDialog}
        onOpenChange={(open) => { setShowEditDialog(open); if (!open) setEditingInterest(null); }}
        interest={editingInterest}
        onChange={setEditingInterest}
        onSubmit={handleEditInterest}
        onDelete={handleDeleteInterest}
      />

      <AchieveDialog
        open={showAchieveDialog}
        onOpenChange={setShowAchieveDialog}
        interest={achievingInterest}
        beneficiary={beneficiary}
        onBeneficiaryChange={setBeneficiary}
        onSubmit={handleAchieveInterest}
      />

      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        onComplete={handleWelcomeComplete}
      />

      <InteractiveWalkthrough
        isActive={showWalkthrough}
        onComplete={() => setShowWalkthrough(false)}
        onSkip={() => setShowWalkthrough(false)}
      />

      <WellbeingSurveyDialog
        open={showSurveyDialog}
        onOpenChange={setShowSurveyDialog}
        data={surveyData}
        onChange={setSurveyData}
        onSubmit={handleSubmitSurvey}
      />

      <MedalCelebration
        medal={earnedMedal}
        onClose={() => setEarnedMedal(null)}
      />
    </div>
  );
};
